import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import classnames from 'classnames';
import { DayPickerRangeController, FocusedInputShape } from 'react-dates';

import './react_dates_overrides.css';
import defaultStyles from '../../styles/formSetStyles.module.css';
import styles from './index.module.css';

import { Button, Margin } from '../../components/Button';
import TimeInput from './TimeInput';

interface Props {
  startDay?: Date;
  endDay?: Date;
  startTime?: Date;
  endTime?: Date;
  showTimeRange?: boolean;
  onFiltersChange: (filters: Filters) => void;
  daysAllowedFrom: number;
  daysAllowedTo?: number;
}

interface Filters {
  startDay?: Date;
  endDay?: Date;
  startTime?: Date;
  endTime?: Date;
}

const DateRangePicker: React.FC<Props> = ({
  startDay,
  endDay,
  startTime,
  endTime,
  showTimeRange,
  onFiltersChange,
  daysAllowedFrom,
  daysAllowedTo,
}) => {
  const { t } = useTranslation();
  const [focusedInput, setFocusedInput] =
    useState<FocusedInputShape>('startDate');
  const [startInputDay, setStartInputDay] = useState<Moment | null>(
    startDay ? moment(startDay) : null,
  );
  const [endInputDay, setEndInputDay] = useState<Moment | null>(
    endDay ? moment(endDay) : null,
  );
  const [startInputTime, setStartInputTime] = useState<Moment | null>(
    startTime ? moment(startTime) : null,
  );
  const [endInputTime, setEndInputTime] = useState<Moment | null>(
    endTime ? moment(endTime) : null,
  );

  const isDayDisabled = (day: Moment): boolean => {
    const dayDiff = day.startOf('day').diff(moment().startOf('day'), 'days');
    return (
      moment().startOf('day').isAfter(day.startOf('day')) ||
      dayDiff < daysAllowedFrom ||
      (!!daysAllowedTo && dayDiff >= daysAllowedTo)
    );
  };

  return (
    <div>
      <div className={styles.LabelWrapper}>
        <div className={defaultStyles.Label}>{t('Dates')}</div>
      </div>
      <DayPickerRangeController
        focusedInput={focusedInput}
        onFocusChange={(input) => {
          setFocusedInput(input || 'startDate');
        }}
        onDatesChange={({ startDate, endDate }) => {
          setStartInputDay(startDate?.startOf('day') || null);
          setEndInputDay(endDate?.endOf('day') || null);
        }}
        startDate={startInputDay}
        endDate={endInputDay}
        initialVisibleMonth={() => moment().add(daysAllowedFrom, 'days')}
        noBorder={true}
        isDayBlocked={(d) => isDayDisabled(d)}
        weekDayFormat="ddd"
        minimumNights={0}
      />
      {showTimeRange && (
        <>
          <div className={styles.LabelWrapper}>
            <div className={defaultStyles.Label}>{t('Times')}</div>
          </div>
          <div className={styles.TimeFilters}>
            <TimeInput
              time={startInputTime}
              label={t('From')}
              onChange={(time) => {
                setStartInputTime(time);
              }}
            />
            <TimeInput
              time={endInputTime}
              label={t('To')}
              onChange={(time) => {
                setEndInputTime(time);
              }}
            />
          </div>
        </>
      )}
      <div
        className={classnames(
          defaultStyles.ButtonSubmitWrapper,
          styles.SubmitFilters,
        )}
      >
        <Button
          type="button"
          children={t('Apply')}
          onClick={() => {
            onFiltersChange({
              startDay: startInputDay?.toDate(),
              endDay: endInputDay?.toDate(),
              startTime: startInputTime?.toDate(),
              endTime: endInputTime?.toDate(),
            });
          }}
          margin={Margin.Modal}
          disabled={
            !startInputDay && !endInputDay && !startInputTime && !endInputTime
          }
        />
      </div>
    </div>
  );
};

export default DateRangePicker;
